import { Modal } from 'almanac-ui/Modal'
import Styles from './SearchModal.module.scss'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RecentFiles } from './RecentFiles'
import { SearchConfigProps, SearchStateProps } from 'components/WorkspaceDashboardSearchBar/Types'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { SearchResultType } from 'components/Search/constants'
import { Configure, InstantSearch } from 'react-instantsearch-dom'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import Foco from 'react-foco'
import { SearchErrorCatcher } from './components/SearchErrorCatcher'
import { ConnectedSearchInput as SearchInput } from './components/SearchInput/SearchInput'
import { getIndexFilters } from './components/useSearch'
import { ConnectedSearchList as SearchList } from './components/SearchList/SearchList'
import { CloseButton } from './components/CloseButton/CloseButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { AlmanacFile, AlmanacFileSummary } from 'types'
import { SearchModalMobileContent } from './SearchModalMobileContent'
import { Button } from 'blaze-ui/Button'

export const SUMMARY_WORDS_LENGTH = 35

interface Props {
  onSuccess?: (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> }) => void
  initialValues?: {
    project?: AlmanacFile | null
    voiceId?: number
  }
}

export const SearchModal = ({ initialValues, onSuccess }: Props) => {
  const auth = useAuth()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const inputRef = useRef<HTMLInputElement>(null)
  const searchBarRef = useRef<HTMLDivElement>(null)
  const [searchState, setSearchState] = useState<SearchStateProps>({ query: '' })
  const [searchValue, setSearchValue] = useState('')
  const client = useAlgoliaClient()
  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const initialSearchConfig = useMemo(() => {
    const config = {
      indexName: `Article_${ALGOLIA_INDEX_SUFFIX}`,
      indexId: 'articles_index',
      name: SearchResultType.Files,
      attributesToSnippet: [`plain_text:${SUMMARY_WORDS_LENGTH}`],
      snippetEllipsisText: '…',
      visible: true,
      filtersActive: false,
      hitsPerPage: 20,
      searchAttributes: ['category_names', 'title', 'plain_text'],
      filters: getIndexFilters({ workspaceId: currentWorkspaceId, auth }),
    }

    return config
  }, [currentWorkspaceId, auth])

  const [searchConfiguration, setSearchConfiguration] = useState<SearchConfigProps>(initialSearchConfig)

  useEffect(() => {
    setSearchConfiguration(initialSearchConfig)
  }, [initialSearchConfig])

  const clearSearchQuery = useCallback(() => {
    setSearchValue('')
    setSearchState({ query: '' })
  }, [])

  const handleReset = useCallback(() => {
    clearSearchQuery()
    inputRef.current?.focus()
  }, [inputRef, clearSearchQuery])

  return (
    <>
      {!isMobile && <CloseButton />}
      <Modal.Root size="lg" bodyClassName={Styles.modalBody} aria-labelledby="create-new-header">
        <InstantSearch
          key={searchConfiguration?.name}
          indexName={searchConfiguration?.indexName}
          searchClient={client}
          searchState={searchState}
          onSearchStateChange={(searchState: SearchStateProps) => {
            setSearchState(searchState)
          }}
        >
          <Configure
            key={searchConfiguration?.name}
            hitsPerPage={searchConfiguration?.hitsPerPage}
            restrictSearchableAttributes={searchConfiguration?.searchAttributes}
            filters={searchConfiguration?.filters}
            attributesToSnippet={searchConfiguration?.attributesToSnippet}
            snippetEllipsisText={searchConfiguration?.snippetEllipsisText}
          />
          {isMobile ? (
            <SearchModalMobileContent
              initialValues={initialValues}
              onSuccess={onSuccess}
              placeholder="Search for files and projects..."
              handleReset={handleReset}
              setSearchConfiguration={setSearchConfiguration}
              searchConfiguration={searchConfiguration}
              setSearchState={setSearchState}
              searchState={searchState}
            />
          ) : (
            <>
              <Foco>
                <div className={Styles.inputWrapper} ref={searchBarRef}>
                  <SearchInput
                    placeholder="Search for files and projects..."
                    initialValue={searchValue || searchState.query}
                    onInputChange={(value: string) => setSearchValue(value.trim())}
                    inputRef={inputRef}
                  />
                  {searchValue.length > 0 && (
                    <Button variant="tertiary" onPress={clearSearchQuery}>
                      Clear Search
                    </Button>
                  )}
                </div>
              </Foco>

              <div className={Styles.contentWrapper}>
                {searchValue.length === 0 ? (
                  <RecentFiles />
                ) : (
                  <div className={Styles.searchResults}>
                    <SearchErrorCatcher />

                    <div className={Styles.searchContainer}>
                      <SearchList
                        query={searchState.query}
                        onReset={handleReset}
                        searchConfiguration={searchConfiguration}
                        setSearchConfiguration={setSearchConfiguration}
                        setSearchState={setSearchState}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </InstantSearch>
      </Modal.Root>
    </>
  )
}
